import React, { ReactNode } from 'react';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';

import { IContentfulLink, Tag } from '../../../types/shared';

import Link from '../../atoms/link/Link';
import TagList from '../../atoms/tag-list/TagList';

import { DEQ_TYPES, trackClickEvent } from '../../../utils/deq';
import { getCtaLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body, H3 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';
import { Theme } from '../../../styles/themes';
import { DefaultFontSize } from '../../../styles/themes/fonts';

import Radiusses from '../../../tokens/Radiusses';

interface Props {
  index: number;
  image: string | IGatsbyImageData;
  title?: string;
  tags?: Tag[];
  ctaLinkPrimary?: IContentfulLink;
  ctaLinkSecondary?: IContentfulLink;
  children?: ReactNode;
  numberOfSiblings?: number;
  isFullWidth?: boolean;
  className?: string;
}

const PreviewCard = ({
  index,
  image,
  title,
  tags,
  ctaLinkPrimary,
  ctaLinkSecondary,
  children,
  numberOfSiblings,
  isFullWidth = false,
  className,
}: Props) => {
  const stringImage = String(image);

  const productPrimaryLink = ctaLinkPrimary
    ? getCtaLink(ctaLinkPrimary)
    : undefined;
  const productSecondaryLink = ctaLinkSecondary
    ? getCtaLink(ctaLinkSecondary)
    : undefined;
  const showCardAsFullWidth = isFullWidth || numberOfSiblings === 1;

  const returnImage = (
    cardImage: string | IGatsbyImageData,
    fullWidth?: boolean
  ) => {
    if (stringImage.includes('.svg')) {
      return (
        <IconTitleWrapper cardRowAlignment={fullWidth}>
          <SvgWrapper biggerIcon={fullWidth}>
            <SvgImage src={cardImage as string} />
          </SvgWrapper>
          {title && (
            <SvgTitle data-testid="title" colorWhite={fullWidth}>
              {title}
            </SvgTitle>
          )}
        </IconTitleWrapper>
      );
    }
    if (cardImage as IGatsbyImageData)
      return (
        <ImageContainer cardRowAlignment={fullWidth}>
          <GatsbyImage
            alt=""
            image={{
              ...(cardImage as IGatsbyImageData),
              layout: 'fullWidth',
            }}
            style={{ width: '100%', height: '100%' }}
            imgStyle={{
              objectFit: 'cover',
            }}
          />
        </ImageContainer>
      );
  };

  return (
    <LinkContainer
      id={`product-id-${index}`}
      key={`product-key-${index}`}
      href={productPrimaryLink || productSecondaryLink || '/404/'}
      customClick={() =>
        trackClickEvent(productPrimaryLink || productSecondaryLink)
      }
      trackingComponent="sales-block"
      trackingType={DEQ_TYPES.btnCard}
      trackingLabel={title || ''}
      data-testid="link"
      fullWidth={showCardAsFullWidth && !stringImage.includes('.svg')}
      cardRowAlignment={showCardAsFullWidth && stringImage.includes('.svg')}
      className={className}
    >
      {returnImage(image, showCardAsFullWidth)}
      <TextContainer
        data-testid="text-container"
        centerAlign={showCardAsFullWidth}
      >
        {!!tags && tags?.length > 0 ? <TagList tags={tags} /> : undefined}
        {!stringImage.includes('.svg') && title && (
          <Title data-testid="title">{title}</Title>
        )}
        <StyledBody>{children}</StyledBody>
      </TextContainer>
    </LinkContainer>
  );
};

export default PreviewCard;

const LinkContainer = styled(Link).withConfig({
  shouldForwardProp: (prop) =>
    !['fullWidth', 'cardRowAlignment'].includes(prop),
})<{ fullWidth?: boolean }>`
  background: ${Colors.white};
  border-radius: ${({ theme }) => theme.radiuses.M};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationNewCard};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  width: 100%;

  ${mq.from.breakpoint.M`
    ${({ fullWidth }: { fullWidth?: boolean }) =>
      fullWidth
        ? css`
            flex-direction: row;
            width: 100%;
          `
        : css`
            flex-direction: column;
            max-width: 622px;
            width: auto;
          `}
  `}

  img {
    transform: scale(1);
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.1);
    transition: transform 0.3s;
  }

  ${mq.from.breakpoint.L`
    ${({ cardRowAlignment }: { cardRowAlignment?: boolean }) =>
      cardRowAlignment
        ? css`
            flex-direction: row;
            width: 100%;
          `
        : undefined}
  `}
`;

const IconTitleWrapper = styled.div<{ cardRowAlignment?: boolean }>`
  background: ${Colors.abnMutedGreen50};
  border-radius: ${({ theme }) => theme.radiuses.M}
    ${({ theme }) => theme.radiuses.M} 0 0;

  ${mq.from.breakpoint.M`
    padding: ${Spacings.M} 0;
  `}

  ${mq.from.breakpoint.L`
    ${({ cardRowAlignment }: { cardRowAlignment?: boolean }) =>
      cardRowAlignment
        ? css`
            background-color: ${({ theme }) => theme.colors.abnGreen200};
            border-bottom-left-radius: ${Radiusses.M};
            border-bottom-right-radius: 0;
            border-top-left-radius: ${Radiusses.M};
            border-top-right-radius: 0;
            display: flex;
            flex-direction: column;
            height: 424px;
            justify-content: center;
            width: 50%;
          `
        : undefined};
  `}
`;

const SvgWrapper = styled.div<{ biggerIcon?: boolean }>`
  margin: 0 auto;
  max-height: 104px;
  max-width: 104px;
  padding: 24px 0 8px;
  position: relative;

  ${mq.from.breakpoint.M`
    padding: 0 0 8px;
  `}

  ${mq.from.breakpoint.L`
    ${({ biggerIcon }: { biggerIcon?: boolean }) =>
      biggerIcon
        ? css`
            max-height: 150px;
            max-width: 150px;
          `
        : undefined}
  `}
`;

const SvgImage = styled.img`
  height: 100%;
  object-fit: cover;
  transition: all 0.1s ease-in-out;
  width: 100%;
`;

const TextContainer = styled.div<{ centerAlign?: boolean }>`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
  padding: 24px;

  ${mq.from.breakpoint.M`
    justify-content: ${({ centerAlign }: { centerAlign?: boolean }) =>
      centerAlign ? 'center' : 'flex-start'};
  `}

  ${mq.from.breakpoint.XL`
    padding: 32px 40px;
  `}
`;

const ImageContainer = styled.div<{ cardRowAlignment?: boolean }>`
  height: 174px;

  ${mq.from.breakpoint.M`
    ${({ cardRowAlignment }: { cardRowAlignment?: boolean }) =>
      cardRowAlignment
        ? css`
            height: 424px;
            width: 50%;
          `
        : undefined}
  `}
`;

const StyledBody = styled(Body)`
  margin-bottom: 0;

  p ~ ul,
  ul {
    padding-top: 0;

    > li {
      margin-bottom: 4px;
    }
  }
`;

const SvgTitle = styled(H3)<{ colorWhite?: boolean }>`
  color: ${Colors.abnMutedGreen500};
  margin-bottom: 24px;
  margin-top: 0;
  padding: 0 16px;
  text-align: center;

  ${mq.from.breakpoint.M`
    margin-bottom: 0;
  `}

  ${mq.from.breakpoint.L`
    ${({ colorWhite }: { colorWhite?: boolean }) =>
      colorWhite
        ? css`
            color: ${({ theme }) => theme.colors.abnNeutralWhite};
          `
        : css`
            color: ${Colors.abnMutedGreen500};
          `}
  `}
`;

const Title = styled(H3)`
  font-size: ${({ theme }) => theme.h3.fontSize.L};
  line-height: ${({ theme }) => theme.h3.lineHeight.L};
  margin-bottom: ${Spacings.XS};
  margin-top: 0;

  ${mq.from.breakpoint.L`
    font-size: ${({ theme }: { theme: Theme }) =>
      theme.h2.fontSize?.L || DefaultFontSize.SIZE};
    line-height: ${({ theme }: { theme: Theme }) =>
      theme.h2.lineHeight?.L || DefaultFontSize.LINE_HEIGHT};
  `}
`;
